import { motion } from 'framer-motion';
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { verifyCode, sendVerificationCode, storeTokens } from '@/services/authService';
import { useAuth } from '@/features/authentication/contexts/AuthContext';
import OTPInput from '@/components/ui/OTPInput';
import BackArrow from '@/components/ui/BackArrow';
import Button from '@/components/ui/Button';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import ProgressIndicator from '@/components/ui/ProgressIndicator';
import BackgroundDecoration from '@/components/ui/BackgroundDecoration';
import { cn } from '@/utils/cn';
import { formatTime } from '@/utils/otpUtils';
import { useOTPTimer } from '@/hooks/useOTPTimer';
import { pageContainer, pageItem } from '@/utils/animations';
import { clearHistoryStack } from '@/utils/navigationHistory';
import { analyticsService } from '@/services/analyticsService';

interface OnboardingOTPVerificationProps {
  className?: string;
}

const OnboardingOTPVerification = ({ className }: OnboardingOTPVerificationProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { updateUser, login, refreshAuthState } = useAuth();
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const verifyInFlight = useRef(false);

  // Get phone_number from navigation state or sessionStorage (after refresh)
  const phone_number = location.state?.phone_number || sessionStorage.getItem('onboarding_phone_number') || '';

  // Use OTP timer hook with 60 seconds (1 minute)
  const { timeLeft, canResend, resetTimer } = useOTPTimer({ initialTime: 60 });

  // جایگزینی صفحه فعلی در history تا کاربر نتواند به صفحات قبلی برگردد
  useEffect(() => {
    window.history.replaceState(null, '', '/onboarding/otp-verification');
  }, []);

  // A verified OTP is consumed by the server. If navigation/back-stack brings
  // this screen back, continue the registration flow instead of verifying it
  // a second time.
  useEffect(() => {
    const verifiedPhone = sessionStorage.getItem('onboarding_phone_verified');
    if (verifiedPhone && verifiedPhone === phone_number) {
      navigate('/onboarding/name', { replace: true });
    }
  }, [navigate, phone_number]);

  // تبدیل اعداد انگلیسی به فارسی
  const toPersianDigits = (str: string): string => {
    const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
    return str.replace(/\d/g, (digit) => persianDigits[parseInt(digit)]);
  };

  const handleOTPChange = (value: string) => {
    setOtp(value);
    
    // Clear error when user starts typing
    if (error && value.trim()) {
      setError('');
    }
  };

  const handleVerify = async () => {
    if (otp.length !== 6) {
      setError('لطفاً کد ۶ رقمی را کامل وارد کنید');
      return;
    }

    if (!phone_number) {
      setError('شماره همراه یافت نشد. لطفاً دوباره تلاش کنید.');
      return;
    }

    // State updates are asynchronous; this ref closes the double-submit race.
    if (verifyInFlight.current) return;
    verifyInFlight.current = true;

    setIsLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      console.log('🔐 [OnboardingOTPVerification] Verifying code:', { phone_number, code: otp });
      
      // Verify OTP code
      const response = await verifyCode(phone_number, otp);
      
      console.log('✅ [OnboardingOTPVerification] Code verified successfully:', response);
      
      if (response.status === 'success') {
        void analyticsService.logEvent('onboarding_otp_verified', {
          action: response.action || 'unknown',
        });
        // Show success message
        setSuccessMessage('ایول وارد شدی');
        
        if (response.action === 'login') {
          // User already exists - login flow
          console.log('🔑 [OnboardingOTPVerification] Existing user - logging in');
          
          // Store tokens - BOTH should be in response body now
          if (response.access_token && response.refresh_token) {
            console.log('💾 [OnboardingOTP] Storing tokens:', {
              hasAccessToken: !!response.access_token,
              hasRefreshToken: !!response.refresh_token,
              expiresIn: response.expires_in
            });
            
            await storeTokens(
              response.access_token,
              response.refresh_token,
              response.expires_in || 300  // 5 minutes for testing
            );
            await analyticsService.setUserIdFromAccessToken(response.access_token);
            
            console.log('✅ [OnboardingOTP] JWT tokens stored successfully');
          } else {
            console.error('❌ [OnboardingOTP] Missing tokens in response');
            throw new Error('Tokens not received from server');
          }
          
          console.log('💾 [OnboardingOTPVerification] JWT tokens stored successfully');
          
          // Update auth context with user login
          login({
            phone_number: response.phone_number,
            authMethod: 'phone',
            is_guest: false,
            is_registered: true
          });
          
          // CRITICAL: Refresh auth state to get updated profile from server
          try {
            await refreshAuthState();
            console.log('✅ [OnboardingOTPVerification] Auth state refreshed from server');
          } catch (e) {
            console.warn('⚠️ [OnboardingOTPVerification] Failed to refresh auth state', e);
          }

          // Refresh avatar from server before navigating
          try {
            const refreshAvatar = (window as any).refreshAvatar;
            if (typeof refreshAvatar === 'function') {
              await refreshAvatar();
              console.log('🖼️ [OnboardingOTPVerification] Avatar refreshed after login');
            }
          } catch (e) {
            console.warn('⚠️ [OnboardingOTPVerification] Failed to refresh avatar after login', e);
          }

          console.log('🏠 [OnboardingOTPVerification] Navigating to dashboard');
          
          // پاک کردن history stack قبل از رفتن به dashboard
          clearHistoryStack('/dashboard');
          navigate('/dashboard', { replace: true });
        } else if (response.action === 'register') {
          // New user - registration flow
          console.log('📝 [OnboardingOTPVerification] New user - continuing registration');
          
          // For registration flow, only store phone_number for next registration steps
          // Do NOT store JWT tokens yet - they will be provided after completing registration
          updateUser({
            phone_number: response.phone_number,
            authMethod: 'phone'
          });

          sessionStorage.setItem('onboarding_phone_verified', response.phone_number);
          
          // Save phone number to sessionStorage for refresh recovery
          sessionStorage.setItem('onboarding_phone_number', response.phone_number);
          
          console.log('📝 [OnboardingOTPVerification] Phone verified for registration, proceeding to name input');
          
          // Navigate to name input
          console.log('👤 [OnboardingOTPVerification] Navigating to name input');
          navigate('/onboarding/name', { replace: true });
        } else {
          throw new Error('نوع عملیات نامشخص است');
        }
      } else {
        throw new Error(response.message || 'خطا در تایید کد');
      }
    } catch (error) {
      console.error('❌ [OnboardingOTPVerification] Error verifying code:', error);
      setError(error instanceof Error ? error.message : 'کد وارد شده صحیح نیست. لطفاً دوباره تلاش کنید.');
    } finally {
      verifyInFlight.current = false;
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!canResend || !phone_number) return;

    setIsLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      console.log('📧 [OnboardingOTPVerification] Resending verification code to:', phone_number);
      
      // Resend verification code
      await sendVerificationCode(phone_number);
      
      console.log('✅ [OnboardingOTPVerification] Code resent successfully');
      
      // Reset timer and clear OTP
      resetTimer();
      setOtp('');
      sessionStorage.removeItem('onboarding_phone_verified');
      
      setSuccessMessage('کد رو فرستادم دوباره');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('❌ [OnboardingOTPVerification] Error resending code:', error);
      setError(error instanceof Error ? error.message : 'خطا در ارسال مجدد کد. لطفاً دوباره تلاش کنید.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/onboarding/phone');
  };

  return (
    <motion.div
      className={cn(
        'min-h-screen bg-white flex flex-col',
        'px-7 py-12',
        'relative overflow-hidden',
        className
      )}
      variants={pageContainer}
      initial="hidden"
      animate="visible"
      dir="rtl"
    >
      {/* Back arrow in top-right corner for RTL */}
      <motion.div
        className="absolute top-6 right-6 z-10"
        variants={pageItem}
      >
        <BackArrow onClick={handleBack} variant="simple" />
      </motion.div>

      {/* Progress indicator */}
      <motion.div
        className="mb-16"
        variants={pageItem}
      >
        <ProgressIndicator currentStep={2} totalSteps={4} />
      </motion.div>

      {/* Main content container */}
      <div className="flex-1 flex flex-col items-center justify-center w-full max-w-auth-mobile md:max-w-auth-tablet lg:max-w-auth-desktop xl:max-w-auth-desktop-lg mx-auto">

        {/* Instruction text */}
        <motion.div
          className="text-center mb-8"
          variants={pageItem}
        >
          <p className={cn(
            'text-base lg:text-lg text-muted leading-relaxed',
            'font-persian'
          )}>
            برای ورود به بوکا کدی که برات به شماره
          </p>
          <p className={cn(
            'text-lg lg:text-xl font-medium text-foreground mt-2',
            'font-persian'
          )}>
            {toPersianDigits(phone_number)}
          </p>
          <p className={cn(
            'text-base lg:text-lg text-muted mt-1',
            'font-persian'
          )}>
            فرستادیم رو وارد کن
          </p>
        </motion.div>

        {/* OTP Input */}
        <motion.div
          className="w-full mb-4"
          variants={pageItem}
        >
          <OTPInput
            length={6}
            value={otp}
            onChange={handleOTPChange}
            error=""
            disabled={isLoading}
          />
        </motion.div>

        {/* Success/Error Messages - زیر اینپوت */}
        {(successMessage || error) && (
          <motion.div
            className="mb-6 text-center"
            variants={pageItem}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {successMessage && (
              <p className="text-green-600 text-sm font-persian">
                {successMessage}
              </p>
            )}
            {error && (
              <div className="space-y-2">
                <p className="text-red-600 text-sm font-persian">
                  {error}
                </p>
              </div>
            )}
          </motion.div>
        )}

        {/* Resend timer */}
        <motion.div
          className="text-center mb-6"
          variants={pageItem}
        >
          {!canResend ? (
            <p className={cn(
              'text-sm text-muted',
              'font-persian'
            )}>
              ارسال مجدد کد تا {toPersianDigits(formatTime(timeLeft))} ثانیه دیگر
            </p>
          ) : (
            <Button
              variant="secondary"
              size="sm"
              onClick={handleResendCode}
              disabled={isLoading}
              className="font-persian"
            >
              ارسال مجدد کد
            </Button>
          )}
        </motion.div>

        {/* Verify Button */}
        <motion.div
          className="w-full"
          variants={pageItem}
        >
          <Button
            variant="primary"
            size="md"
            className="w-full rounded-xl"
            onClick={handleVerify}
            disabled={isLoading || otp.length !== 6}
          >
            {isLoading ? (
              <div className="flex items-center gap-2">
                <LoadingSpinner size="sm" />
                <span>در حال تأیید...</span>
              </div>
            ) : (
              'تایید'
            )}
          </Button>
        </motion.div>
      </div>

      {/* Background decoration */}
      <BackgroundDecoration />
    </motion.div>
  );
};

export default OnboardingOTPVerification;
